/**
 * Where a filename ends and its format begins.
 *
 * Shared because both halves of the system need the same answer and would
 * drift apart holding their own: the server resolves collisions with it
 * ("Invoice" and "Invoice.xlsx" must not both become "Invoice (1)xlsx"), and
 * the Drive hides extensions with it, the way a file explorer does. A listing
 * that disagrees with the server about what a file is called is a listing that
 * lies about which file you are looking at.
 */
/**
 * Split a filename into the part a person named and the part the format did.
 *
 * Only a real extension is split off — a short tail after a dot, containing at
 * least one letter, that is not the whole name. The letter is what separates
 * ".xlsx" and ".7z" from "Invoice.2026", where the tail is a year and cutting
 * it off would rename the document. ".gitignore" keeps its leading dot for the
 * same reason: there is no base name in front of it.
 */
export function splitExtension(filename) {
    const match = /^(.*[^.])(\.[A-Za-z0-9]{1,8})$/.exec(filename);
    if (!match)
        return { base: filename, extension: '' };
    const extension = match[2];
    if (!/[A-Za-z]/.test(extension))
        return { base: filename, extension: '' };
    return { base: match[1], extension };
}
/** A filename without its extension, for display. */
export function baseName(filename) {
    return splitExtension(filename).base;
}
/**
 * A zip, which is the one kind of file the Drive can unpack.
 *
 * Shared so the row that offers "Extract here" and the route that does it
 * cannot disagree about what an archive is. The mime type is consulted where
 * there is one — a zip uploaded with no extension still has magic bytes, and
 * the Drive records what it detected rather than what it was called.
 *
 * .rar, .7z and .tar.gz are deliberately not here. Each needs its own decoder,
 * none has turned up in the client's paperwork, and offering Extract on a file
 * that then fails is worse than not offering it.
 */
export function isArchive(filename, mimeType) {
    if (/\.zip$/i.test(filename))
        return true;
    return mimeType === 'application/zip' || mimeType === 'application/x-zip-compressed';
}
//# sourceMappingURL=filenames.js.map